import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ExpenseDTO, PaymentMethod } from '@shared/types';

interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ExpenseFilters {
  month?: string;
  from?: string;
  to?: string;
  categoryId?: number;
  debtId?: number;
  page?: number;
}

export function useExpenses(filters: ExpenseFilters = {}) {
  return useQuery({
    queryKey: ['expenses', filters],
    queryFn: async () => (await api.get<Paginated<ExpenseDTO>>('/expenses', { params: filters })).data,
  });
}

export interface CreateExpenseInput {
  categoryId?: number | null;
  debtId?: number | null;
  amount: number;
  description: string;
  spentAt: string;
  paymentMethod?: PaymentMethod;
  installmentsTotal?: number | null;
  generatesInterest?: boolean;
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateExpenseInput) => (await api.post<ExpenseDTO>('/expenses', input)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`/expenses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
